import { useCallback, useState } from "react";

/**
 * State for a drawer that mounts when it opens and unmounts only after it has
 * slid out, so its exit transition runs and nothing inside it fetches while
 * closed. Mount on the record, drive with `open`:
 *
 * @example
 * const invoice = useDrawerRecord<string>();
 * <Row onClick={() => invoice.show(row.id)} />
 * {invoice.record && (
 *   <InvoiceDetailsDrawer
 *     open={invoice.open}
 *     invoiceId={invoice.record}
 *     handleCloseInvoiceDetailsDrawer={invoice.close}
 *     onAfterClose={invoice.afterClose}
 *   />
 * )}
 */
export function useDrawerRecord<T>() {
  const [record, setRecord] = useState<T>();
  const [open, setOpen] = useState(false);

  const show = useCallback((next: T) => {
    setRecord(next);
    setOpen(true);
  }, []);
  // Keeps the record, so the content stays on screen while the panel slides out.
  const close = useCallback(() => setOpen(false), []);
  const afterClose = useCallback(() => setRecord(undefined), []);

  return { record, open, show, close, afterClose };
}

export default useDrawerRecord;
